import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiRequest } from '../lib/queryClient';
import { SubscriptionStatus, SubscriptionContextType } from '../types/subscription';
import { useAuth } from '../hooks/use-auth';

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  // Fetch user subscription status
  const {
    data: userSubscription,
    isLoading: userLoading,
    error: userError,
    refetch: refetchUserSubscription
  } = useQuery<SubscriptionStatus>({
    queryKey: ['/api/user/subscription-status'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/user/subscription-status');
      if (!res.ok) {
        if (res.status === 404) {
          // Return default subscription status if endpoint doesn't exist yet
          return {
            has_active: false,
            features: {
              scope1: true,
              scope2: true,
              scope3: false,
              advanced_analytics: false,
              priority_support: false
            }
          };
        }
        throw new Error(`HTTP ${res.status}`);
      }
      return await res.json();
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch company subscriptions (we'll need to get user's companies first)
  const {
    data: companies,
    isLoading: companiesLoading
  } = useQuery({
    queryKey: ['/api/users/me/companies'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/users/me/companies');
      if (!res.ok) {
        if (res.status === 401) {
          // User not authenticated, return empty array
          return [];
        }
        throw new Error(`HTTP ${res.status}`);
      }
      return await res.json();
    },
    enabled: !!user,
  });

  // Fetch subscription status for each company
  const companySubscriptions: Record<number, SubscriptionStatus> = {};
  
  // Use a single query to fetch all company subscriptions
  const {
    data: allCompanySubscriptions,
    isLoading: companySubsLoading,
    error: companySubsError,
    refetch: refetchCompanySubscriptions
  } = useQuery<Record<number, SubscriptionStatus>>({
    queryKey: ['/api/company-subscriptions'],
    queryFn: async () => {
      if (!companies || companies.length === 0) return {};
      
      const subscriptions: Record<number, SubscriptionStatus> = {};
      
      // Fetch subscription for each company
      for (const company of companies) {
        try {
          const res = await apiRequest('GET', `/api/company/${company.id}/subscription-status`);
          if (res.ok) {
            subscriptions[company.id] = await res.json();
          } else if (res.status === 404) {
            // Return default subscription status if endpoint doesn't exist yet
            subscriptions[company.id] = {
              has_active: false,
              features: {
                scope1: true,
                scope2: true,
                scope3: false,
                advanced_analytics: false,
                priority_support: false
              }
            };
          } else if (res.status === 403) {
            // User doesn't have access to this company - skip silently
            console.log(`No access to company ${company.id}, skipping subscription check`);
            continue;
          }
        } catch (error) {
          // Check if it's a 403 error (access denied)
          if (error instanceof Error && error.message && error.message.includes('403')) {
            console.log(`No access to company ${company.id}, skipping subscription check`);
            continue;
          }
          console.error(`Failed to fetch subscription for company ${company.id}:`, error);
          // Add default subscription status on other errors
          subscriptions[company.id] = {
            has_active: false,
            features: {
              scope1: true,
              scope2: true,
              scope3: false,
              advanced_analytics: false,
              priority_support: false
            }
          };
        }
      }
      
      return subscriptions;
    },
    enabled: !!user && !!companies && companies.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Update companySubscriptions with fetched data
  if (allCompanySubscriptions) {
    Object.assign(companySubscriptions, allCompanySubscriptions);
  }

  const isLoading = userLoading || companiesLoading || companySubsLoading;
  const error = userError || companySubsError;

  // Helper function to check if user has a specific feature
  const checkFeature = (feature: string): boolean => {
    // Check user subscription first
    if (userSubscription?.has_active && userSubscription.features[feature]) {
      return true;
    }

    // Check company subscriptions
    for (const companyId in companySubscriptions) {
      const companySub = companySubscriptions[companyId];
      if (companySub.has_active && companySub.features[feature]) {
        return true;
      }
    }

    return false;
  };

  // Convenience flags
  const hasScope3Access = checkFeature('scope3');
  const hasAdvancedAnalytics = checkFeature('advanced_analytics');
  const hasPrioritySupport = checkFeature('priority_support');

  // Refresh all subscriptions
  const refreshSubscriptions = () => {
    refetchUserSubscription();
    refetchCompanySubscriptions();
  };

  const contextValue: SubscriptionContextType = {
    userSubscription: userSubscription || null,
    companySubscriptions,
    isLoading,
    error: error as Error | null,
    checkFeature,
    hasScope3Access,
    hasAdvancedAnalytics,
    hasPrioritySupport,
    refreshSubscriptions,
  };

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
